import { Box, Divider, Heading, Paragraph, Text } from '@zillow/constellation-10';

import {
  RenterMoveInCostsDetailsInterface,
  RenterPaymentMethodInterface,
  RenterPaymentSubscriptionInterface,
} from '@renter-hub/models/Types';
import { getFormattedPaymentMethod } from '@common/constants/PaymentMethod';

interface Props {
  paymentMethodToDelete: RenterPaymentMethodInterface;
  selectedMoveInCostsPaymentMethodDetails: RenterPaymentMethodInterface | null;
  selectedMonthlyRentPaymentMethodDetails: RenterPaymentMethodInterface | null;
  formattedAddress: string;
  paymentSubscriptionDetails: RenterPaymentSubscriptionInterface;
  moveInCostsDetails: RenterMoveInCostsDetailsInterface;
}

export const DeleteOneOtherActivePaymentMethod = ({
  paymentMethodToDelete,
  formattedAddress,
  selectedMoveInCostsPaymentMethodDetails,
  selectedMonthlyRentPaymentMethodDetails,
  paymentSubscriptionDetails,
  moveInCostsDetails,
}: Props) => {
  // Only show move-in costs if it's scheduled
  const shouldShowMoveInCosts = moveInCostsDetails?.isScheduled && selectedMoveInCostsPaymentMethodDetails;

  // Only show monthly rent if it has payment subscription details
  const shouldShowMonthlyRent =
    paymentSubscriptionDetails?.nextScheduledDate &&
    paymentSubscriptionDetails?.nextScheduledDate !== '0' &&
    selectedMonthlyRentPaymentMethodDetails;

  return (
    <Box css={{ display: 'flex', flexDirection: 'column', gap: 'token(spacing.md)' }}>
      <Heading level={2} textStyle="heading-xs">
        Review and confirm your changes
      </Heading>
      <Box>
        <Paragraph textStyle="body-bold">Delete payment method</Paragraph>
        <Paragraph textStyle="body-lg-bold">{formattedAddress}</Paragraph>
        <Paragraph css={{ color: 'text.subtle' }} textStyle="body-sm">{`${getFormattedPaymentMethod(
          paymentMethodToDelete.accountType,
        )} ending in ${paymentMethodToDelete.accountLast4}`}</Paragraph>
      </Box>
      <Divider />
      <Box>
        <Paragraph textStyle="body-bold">Update payment method</Paragraph>
        <Text>These accounts will now be charged for the following payments:</Text>
        {shouldShowMoveInCosts && (
          <Box
            css={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 'token(spacing.xs)',
              alignItems: 'center',
            }}
          >
            <Paragraph>Move-in payment</Paragraph>
            <Box
              css={{
                textAlign: 'right',
              }}
            >
              <Text>{`${getFormattedPaymentMethod(
                selectedMoveInCostsPaymentMethodDetails?.accountType,
              )} ending in ${selectedMoveInCostsPaymentMethodDetails?.accountLast4}`}</Text>
            </Box>
          </Box>
        )}
        {shouldShowMonthlyRent && (
          <Box
            css={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 'token(spacing.xs)',
              alignItems: 'center',
            }}
          >
            <Paragraph>Monthly rent {`${paymentSubscriptionDetails?.recurring ? '(auto-pay)' : ''}`}</Paragraph>

            <Box
              css={{
                textAlign: 'right',
              }}
            >
              <Text>{`${getFormattedPaymentMethod(
                selectedMonthlyRentPaymentMethodDetails?.accountType,
              )} ending in ${selectedMonthlyRentPaymentMethodDetails?.accountLast4}`}</Text>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};
